import { MapPin, Phone, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import { CLINIC, telLink, whatsappLink } from "@/lib/clinic";

export default function Contact() {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-forest-900 text-sand-50 py-16 md:py-32 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Lead block */}
          <div className="col-span-12 md:col-span-7">
            <span className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-sand-50/70">
              <span className="w-10 h-px bg-terracotta-500" />
              Visit · Call · Heal
            </span>
            <h2 className="mt-5 font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
              Begin your
              <br />
              <em className="text-terracotta-500">recovery</em> today.
            </h2>
            <p className="mt-8 max-w-xl text-lg text-sand-50/80 leading-relaxed">
              We don't believe in waitlists for healing. Send a quick WhatsApp
              note or pick up the phone — we'll find you a slot that fits.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-sand-50 pl-7 pr-5 py-4 text-sm tracking-wide transition-all hover:-translate-y-1"
              >
                <MessageCircle size={18} strokeWidth={1.75} />
                Chat on WhatsApp
                <span className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-sand-50 text-terracotta-500 transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={16} />
                </span>
              </a>
              <a
                href={telLink()}
                data-testid="contact-phone-btn"
                className="inline-flex items-center gap-3 rounded-full border border-sand-50/30 hover:border-sand-50 text-sand-50 px-7 py-4 text-sm tracking-wide transition-all hover:bg-sand-50 hover:text-forest-900"
              >
                <Phone size={16} strokeWidth={1.75} />
                {CLINIC.phoneDisplay}
              </a>
            </div>
          </div>

          {/* Address & Hours */}
          <div className="col-span-12 md:col-span-5 md:pl-8 lg:pl-16">
            <div className="space-y-10">
              <div data-testid="contact-address">
                <div className="flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-sand-50/70">
                  <MapPin size={14} strokeWidth={1.5} />
                  Address
                </div>
                <p className="mt-3 font-serif text-2xl md:text-3xl leading-snug">
                  {CLINIC.address}
                </p>
                <a
                  href="https://maps.google.com/?q=Revive+Ripple+Mylapore+Chennai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-terracotta-500 hover:text-sand-50 transition-colors"
                >
                  Get directions <ArrowUpRight size={14} />
                </a>
              </div>

              <div data-testid="contact-hours">
                <div className="flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-sand-50/70">
                  <Clock size={14} strokeWidth={1.5} />
                  Hours
                </div>
                <ul className="mt-4 divide-y divide-sand-50/10">
                  {CLINIC.hours.map((h) => {
                    const active = h.day === today;
                    const closed = h.time === "Closed";
                    return (
                      <li
                        key={h.day}
                        data-testid={`hour-${h.day.toLowerCase()}`}
                        className={`flex items-center justify-between py-3 ${
                          active ? "text-sand-50" : "text-sand-50/75"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          {active && (
                            <span className="w-1.5 h-1.5 rounded-full bg-terracotta-500" />
                          )}
                          <span className={active ? "font-medium" : ""}>
                            {h.day}
                          </span>
                        </span>
                        <span
                          className={`text-sm ${
                            closed ? "text-sand-50/45" : ""
                          }`}
                        >
                          {h.time}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer line */}
        <div className="mt-24 pt-10 border-t border-sand-50/15 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt="Revive Ripple"
              className="w-12 h-12 rounded-full object-cover ring-1 ring-sand-50/20"
            />
            <span className="font-serif text-2xl">
              Revive <em className="text-sky-400">Ripple</em>
            </span>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase text-sand-50/55">
            © {new Date().getFullYear()} Revive Ripple Physiotherapy · Mylapore, Chennai
          </p>
        </div>
      </div>
    </section>
  );
}
